import type { Dispatch, SetStateAction, FunctionComponent } from "react"
import { Fragment } from "react"
import { Dialog, Transition } from "@headlessui/react"
import { XMarkIcon } from "@heroicons/react/24/outline"
import { Button } from "./ui/button"

export interface FilteringModalProps {
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
  title: string
  filters: JSX.Element
  onClearAll: () => void
  onSubmit: () => void
  isSearchDisabled?: boolean
}

export const FilteringModal: FunctionComponent<FilteringModalProps> = ({
  open,
  setOpen,
  title,
  filters,
  onClearAll,
  onSubmit,
  isSearchDisabled = false,
}) => {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity overflow-hidden" />
        </Transition.Child>

        <div className="fixed inset-0 z-10  overflow-hidden">
          <div className="flex justify-center items-start h-full px-1 pt-16 sm:pb-6 text-center overflow-hidden">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <Dialog.Panel className="relative flex flex-col transform rounded-lg bg-background text-left shadow-xl transition-all w-screen max-h-full sm:my-8 sm:max-w-2xl">
                <div className="absolute right-0 top-0 pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-background text-gray-400 hover:text-gray-500 outline-0"
                    onClick={() => setOpen(false)}>
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-center w-full px-3 pb-3 pt-4 sm:p-4 text-gray-900 border-b">
                  <div className="flex justify-center w-full sm:ml-4 sm:mt-0 sm:text-left">
                    <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-foreground">
                      {title}
                    </Dialog.Title>
                  </div>
                </div>
                <div className="text-xs text-foreground overflow-auto max-h-[45rem] xl:max-h-[40rem] px-6 pt-0 pb-24 sm:pb-8">
                  {filters}
                </div>
                <div className="absolute flex justify-between bottom-0 text-gray-900 border-t sm:relative sm:bottom-auto bg-background sm:rounded-lg w-full px-6 py-4 sm:p-4">
                  <Button onClick={onClearAll} className="text-foreground" variant="ghost">
                    Clear All
                  </Button>
                  <Button onClick={onSubmit} disabled={isSearchDisabled} className="" variant="default">
                    Show Results
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
